import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { X, Printer, ChevronLeft, ChevronRight, ArrowRight, AlertTriangle } from 'lucide-react';
import StatementSheet from './StatementSheet';
import { normalizeStatementItems, totalsOfStatement, STATEMENT_LEDGER_CSS } from '../utils/statementView';
import { printStatement, statementPrintTitle } from '../utils/printStatement';

// 거래명세서 「미리보기」 모달 (2026-10-06, 기획서_치과_명세서미리보기 A안)
// - 읽기 전용: 발행된 양식(StatementSheet)을 그대로 보여주고 인쇄/PDF(브라우저 인쇄 대화상자)만 제공. Firestore 쓰기 0건
// - 정산 처리는 하지 않는다 → [정산 처리하러 가기 →]로 기존 상세(행 클릭과 동일)로 보낸다 (D2)
// - 목록이 들고 있는 문서로 즉시 렌더 → getDoc 1회로 최신본 교체, 실패 시 콘솔 기록 + 안내 1줄 (조용한 실패 금지)
// - PC: 가운데 큰 창(최대 960px, 세로 스크롤) / 768px 미만: 전체 화면. ESC·배경 클릭·[닫기]로 닫힘, 포커스 트랩, ←→ 이전/다음 (D4)
// - 치과 노출 화면 — 홍보·유인 문구 없음(의료법)
//
// props
//   statement     목록이 보유한 문서(id 포함)
//   partnerName   보는 사람 기준 거래처명 (헤더·인쇄 제목)
//   statusBadge   목록의 상태 배지 노드(기존 라벨 재사용)
//   position      { index, total } — "n / N" 표시
//   onPrev/onNext 이전·다음 (없으면 버튼 비활성)
//   canGoDetail   [정산 처리하러 가기] 노출 여부 (수신자·pending/disputed)
//   onGoDetail    상세로 이동
//   onClose       닫기

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function StatementPreviewModal({ statement, partnerName, statusBadge, position, onPrev, onNext, canGoDetail, onGoDetail, onClose }) {
    const [latest, setLatest] = useState(statement);
    const [refreshFailed, setRefreshFailed] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
    const dialogRef = useRef(null);
    const sheetRef = useRef(null);
    const closeBtnRef = useRef(null);

    // 목록 문서로 즉시 표시 → 최신본으로 조용히 교체
    useEffect(() => {
        let cancelled = false;
        setLatest(statement);
        setRefreshFailed(false);
        if (!statement?.id) return undefined;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'transactionStatements', statement.id));
                if (cancelled) return;
                if (snap.exists()) {
                    // direction 등 목록 전용 태그는 유지
                    setLatest({ ...statement, ...snap.data(), id: snap.id });
                } else {
                    console.warn('[StatementPreviewModal] 문서가 없어 목록 시점 내용을 표시합니다:', statement.id);
                    setRefreshFailed(true);
                }
            } catch (e) {
                if (cancelled) return;
                console.error('[StatementPreviewModal] 최신본 조회 실패 — 목록 시점 내용 표시:', statement.id, e);
                setRefreshFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, [statement]);

    // 반응형 (LabProfile.js와 같은 768px 기준)
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // 열린 동안 뒤 화면 스크롤 잠금
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // 처음 열릴 때 닫기 버튼에 포커스 (키보드 사용자)
    useEffect(() => {
        closeBtnRef.current?.focus();
    }, []);

    // ESC 닫기 · ←→ 이전/다음 · Tab 포커스 트랩
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
        if (e.key === 'ArrowLeft' && onPrev) { e.preventDefault(); onPrev(); return; }
        if (e.key === 'ArrowRight' && onNext) { e.preventDefault(); onNext(); return; }
        if (e.key === 'Tab' && dialogRef.current) {
            const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE));
            if (nodes.length === 0) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
    }, [onClose, onPrev, onNext]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const items = useMemo(() => normalizeStatementItems(latest?.items), [latest]);
    const totals = useMemo(() => totalsOfStatement(latest, items), [latest, items]);

    const handlePrint = () => {
        printStatement({ root: sheetRef.current, title: statementPrintTitle(latest, partnerName) });
    };

    if (!latest) return null;

    const isDeletedBySender = !!latest.deletedBySender;
    const posText = position && position.total > 1 ? `${position.index + 1} / ${position.total}` : '';

    const btnBase = {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8,
        fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #E2E8F0', background: '#fff', color: '#334155',
    };
    const navBtn = (disabled) => ({ ...btnBase, padding: '8px 10px', opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' });

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100,
                background: 'rgba(15, 23, 42, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: isMobile ? 0 : 20,
            }}
        >
            <style>{STATEMENT_LEDGER_CSS}</style>
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="statement-preview-title"
                style={{
                    background: '#F8FAFC',
                    width: isMobile ? '100%' : 'min(960px, 94vw)',
                    height: isMobile ? '100%' : 'auto',
                    maxHeight: isMobile ? '100%' : '90vh',
                    borderRadius: isMobile ? 0 : 16,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}
            >
                {/* 헤더 */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: isMobile ? '12px 14px' : '14px 20px', background: '#fff', borderBottom: '1px solid #E2E8F0',
                }}>
                    <h3 id="statement-preview-title" style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#0f172a' }}>
                        거래명세서 미리보기
                    </h3>
                    <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{partnerName || '-'}</span>
                    <span style={{ fontSize: 12, color: '#6366F1', fontWeight: 600 }}>{latest.statementNumber || '-'}</span>
                    {isDeletedBySender ? (
                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                            발행처에서 삭제함
                        </span>
                    ) : statusBadge}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        {posText && <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>{posText}</span>}
                        <button
                            ref={closeBtnRef}
                            onClick={onClose}
                            aria-label="닫기"
                            title="닫기 (ESC)"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#64748B', display: 'inline-flex' }}
                        >
                            <X size={22} />
                        </button>
                    </div>
                </div>

                {/* 본문 (세로 스크롤) */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: isMobile ? 10 : 20 }}>
                    {isDeletedBySender && (
                        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, marginBottom: 14, fontSize: 13.5, color: '#B91C1C', fontWeight: 600 }}>
                            <AlertTriangle size={18} />
                            <span>발행처에서 삭제한 명세서입니다. 정산 처리는 할 수 없습니다.</span>
                        </div>
                    )}
                    {refreshFailed && (
                        <div className="no-print" style={{ padding: '8px 12px', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 8, marginBottom: 12, fontSize: 12.5, color: '#64748B' }}>
                            최신 내용 확인에 실패해 목록 시점 내용을 표시합니다.
                        </div>
                    )}
                    {/* 모바일은 양식이 좁게 접히지 않도록 가로 스크롤 (글자 크기는 상세와 동일 유지) */}
                    <div style={{ minWidth: isMobile ? 640 : undefined }}>
                        <StatementSheet statement={latest} items={items} totals={totals} rootRef={sheetRef} />
                    </div>
                </div>

                {/* 하단 버튼 */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                    padding: isMobile ? '10px 12px' : '12px 20px', background: '#fff', borderTop: '1px solid #E2E8F0',
                }}>
                    <button onClick={onPrev} disabled={!onPrev} style={navBtn(!onPrev)} title="이전 명세서 (←)">
                        <ChevronLeft size={16} /> 이전
                    </button>
                    <button onClick={onNext} disabled={!onNext} style={navBtn(!onNext)} title="다음 명세서 (→)">
                        다음 <ChevronRight size={16} />
                    </button>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                            onClick={handlePrint}
                            title="인쇄 창에서 '대상: PDF로 저장'을 고르면 파일로 남습니다"
                            style={btnBase}
                        >
                            <Printer size={16} /> 인쇄 / PDF 저장
                        </button>
                        {canGoDetail && !isDeletedBySender && (
                            <button
                                onClick={onGoDetail}
                                title="이상 없으면 정산 확인, 다른 부분이 있으면 수정 요청"
                                style={{ ...btnBase, background: '#6366F1', color: '#fff', border: 'none' }}
                            >
                                정산 처리하러 가기 <ArrowRight size={16} />
                            </button>
                        )}
                        <button onClick={onClose} style={btnBase}>
                            닫기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StatementPreviewModal;
